/**
 * A template for somebody who has none.
 *
 * The playground's best moment needs a 585×559 PNG to hand, and a stranger
 * arriving from a search result has nothing — so they get the map and never the
 * upload, which is the half that sells the tool.
 *
 * **Drawn, not shipped.** No asset, no build step, and no rights question: it is
 * generated from `CANVAS` and the measured `REGIONS`, so it is provably the
 * right size and provably lands on the right faces. A checked-in PNG could
 * drift from the map; this cannot.
 *
 * It is deliberately a plain garment rather than a colour chart. The point is to
 * look like something somebody might actually wear for the ten seconds before
 * they drop in their own file.
 */
import { CANVAS, REGION_IDS, type RegionId } from "../../shared/format";
import { REGIONS } from "../../shared/regions";

/** A muted two-tone kit. Chosen to read on the body, not to match the brand. */
const BODY = "#3B5BA5";
const TRIM = "#E8E4D9";
const SHADE = "#31497F";

/** Which faces get the darker shade: the ones that face away from a light. */
const SHADED = new Set<RegionId>(["torso.back", "leftLimb.back", "rightLimb.back"]);

/**
 * Paint one region, then band its lower edge.
 *
 * The band is what makes it read as clothing: a hem on the torso and cuffs on
 * the limbs, in the same places a real garment has them.
 */
function paintRegion(ctx: CanvasRenderingContext2D, id: RegionId) {
  const r = REGIONS[id];
  const [part, face] = id.split(".") as [string, string];

  ctx.fillStyle = SHADED.has(id) ? SHADE : BODY;
  ctx.fillRect(r.x, r.y, r.w, r.h);

  // Caps are the top and bottom of a limb or torso box — a hem across one
  // would be a stripe through the middle of a shoulder, so they stay plain.
  if (face === "up" || face === "down") return;

  const band = part === "torso" ? Math.round(r.h * 0.12) : Math.round(r.h * 0.18);
  ctx.fillStyle = TRIM;
  ctx.fillRect(r.x, r.y + r.h - band, r.w, band);
}

/**
 * The whole sample, at exactly the template size.
 *
 * Every face is painted, including the caps — a sample with blank faces would
 * trip the empty-face note on the way in and teach the wrong lesson about the
 * tool on somebody's first ten seconds with it.
 */
export function drawSample(canvas: HTMLCanvasElement): void {
  canvas.width = CANVAS.width;
  canvas.height = CANVAS.height;
  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  // Transparent everywhere the garment does not reach: the 54.9% that never
  // renders must stay empty, or the sample would teach that it matters.
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  for (const id of REGION_IDS) paintRegion(ctx, id);
}

/** Ready-made, for a caller that just wants something to hand to the viewer. */
export function sampleTemplate(): HTMLCanvasElement {
  const canvas = document.createElement("canvas");
  drawSample(canvas);
  return canvas;
}
